import { firstValueFrom, fromEvent, merge, take } from 'rxjs'
import { action, observable } from 'mobx'
import type { Storage } from '@/infra/storage'
import { toStream } from '@/utils/mobx-util'
import { shareLatest } from '@/utils/rx-util'
import { anyAbortSignal, onAbort, resolveOnAbort } from '@/utils/abort-util'

// Sounds
import soundSourceGotMessageMp3 from '@/assets/sounds/got-message.mp3'
import soundSourceGotMessageOgg from '@/assets/sounds/got-message.ogg'

/**
 * The available sounds.
 */
export type Sound = 'GotMessage'

/**
 * Map of sounds and where to load them.
 */
const sounds = new Map<Sound, SoundSource>([
  [
    'GotMessage',
    {
      mp3: soundSourceGotMessageMp3,
      ogg: soundSourceGotMessageOgg,
    },
  ],
])

interface SoundSource {
  mp3: string
  ogg: string
}

/**
 * The sound player.
 */
export class SoundPlayer {
  /**
   * We can only play sound once we have interacted with the document at least once.
   * We emit the first interaction.
   * @private
   */
  private readonly _ready$ = merge(
    fromEvent(window, 'mousedown', { capture: true }),
    fromEvent(window, 'click', { capture: true }),
    fromEvent(window, 'keypress', { capture: true }),
    fromEvent(window, 'touchstart', { capture: true }),
  ).pipe(
    // Unsubscribe after the first interaction.
    take(1),
    // Once we have the first one, emit that on subscribing.
    shareLatest(),
  )

  /**
   * Whether the sound player is enabled.
   */
  @observable accessor isEnabled = false

  constructor(private readonly _storage: Storage) {
    this.playSound = this.playSound.bind(this)
    this._ready$.subscribe()
    this.setEnabled(_storage.getItem('soundEnabled') !== 'disabled')
    toStream(() => this.isEnabled).subscribe((enabled) =>
      this._storage.setItem('soundEnabled', enabled ? 'enabled' : 'disabled'),
    )
  }

  /**
   * Toggles whether the sound player is enabled.
   *
   * @param enabled
   */
  @action.bound
  setEnabled(enabled: boolean) {
    this.isEnabled = enabled
  }

  /**
   * Plays the specified sound.
   *
   * @param sound
   * @param signal
   */
  async playSound(sound: Sound, signal?: AbortSignal) {
    if (!this.isEnabled || signal?.aborted) {
      return
    }

    // A sound that can't play within 2 seconds is stale.
    const playable = anyAbortSignal(signal, AbortSignal.timeout(2000))
    await resolveOnAbort(
      firstValueFrom(this._ready$, { defaultValue: null }),
      playable,
    )
    if (playable.aborted) {
      return
    }

    const audio = SoundPlayer._getAudio(sound)
    const dispose = onAbort(signal, () => {
      audio.pause()
    })
    audio.addEventListener('ended', dispose, { once: true })

    try {
      audio.currentTime = 0
      await audio.play()
    } catch (err: unknown) {
      dispose()
      console.warn(`Unable to play sound ${sound}`, err)
    }
  }

  /**
   * Gets the audio object for the specified sound.
   *
   * @param sound
   */
  private static _getAudio(sound: Sound) {
    const audio = new window.Audio()
    const type = audio.canPlayType('audio/mpeg') ? 'mp3' : 'ogg'
    audio.volume = 0.6
    audio.src = sounds.get(sound)![type]
    audio.pause()
    return audio
  }
}
